import { CalendarClock } from "lucide-react";
import { setHeading, setText } from "../constants/typography";
import { CONTENT } from "../constants/content";
import Reveal from "./ui/Reveal";
import CtaButton from "./ui/CtaButton";
import Callout from "./ui/Callout";
import AlpsWords from "./ui/AlpsWords";
import Emphasis from "./ui/Emphasis";
import styles from "../styles/About.module.scss";
import teacherImage from "../assets/images/caterina-about3.jpg";
import teacher480 from "../assets/images/caterina-about3-480.webp";
import teacher768 from "../assets/images/caterina-about3-768.webp";
import teacher1200 from "../assets/images/caterina-about3-1200.webp";
import teacher1600 from "../assets/images/caterina-about3-1600.webp";

const TEACHER_SRCSET = [
  `${teacher480} 480w`,
  `${teacher768} 768w`,
  `${teacher1200} 1200w`,
  `${teacher1600} 1600w`,
].join(", ");

/*
  Deliberately larger than the layout width. The source is a 1620x1080
  landscape frame shown through a 4:5 portrait box, so `object-fit: cover`
  throws away roughly half the width — the browser needs about twice the
  file it would need for an uncropped image to fill the box at 2x. Declaring
  the true layout width here made it pick the 1200w file and the portrait
  rendered visibly soft on a retina screen.
*/
const TEACHER_SIZES = "(min-width: 993px) 56vw, 100vw";

/**
 * Two sections, not one.
 *
 * "Chi sono" is the biography: a header, then the portrait beside the copy.
 * "È il percorso giusto per te?" is the qualification step, with its own
 * header, the two contrasting lists and the closing note. They were one
 * section, which made the two panels read as an appendix to the biography
 * rather than as the decision the reader is actually being asked to make.
 */
const About = () => {
  const {
    eyebrow,
    title,
    subtitle,
    fit,
    introduction,
    listIcon: ListIcon,
    isForYou,
    willHelpYou,
    conclusion,
    cta,
  } = CONTENT.about;

  const renderSegments = (paragraph) =>
    paragraph.map((segment, index) =>
      segment.type === "bold" ? (
        <Emphasis key={index}>{segment.content}</Emphasis>
      ) : (
        <AlpsWords key={index}>{segment.content}</AlpsWords>
      )
    );

  const [firstParagraph, ...restParagraphs] = introduction;

  return (
    <>
      <section id="about" className={styles.about} aria-label="Chi Sono">
        <Reveal className={styles.head}>
          <span className={styles.eyebrow}>{eyebrow}</span>
          <h2 className={styles.title} itemProp="name">
            {setHeading(title)}
          </h2>
          <p className={styles.headSubtitle}>{setText(subtitle)}</p>
        </Reveal>

        {/* --- Portrait left, biography right --- */}
        <div className={styles.intro}>
          <Reveal className={styles.portrait} from="right">
            <img
              src={teacherImage}
              srcSet={TEACHER_SRCSET}
              sizes={TEACHER_SIZES}
              width="1620"
              height="1080"
              alt="Caterina Imola, insegnante di italiano per stranieri con oltre 10 anni di esperienza"
              itemProp="image"
              loading="lazy"
              decoding="async"
            />
          </Reveal>

          <div className={styles.introText}>
            {firstParagraph && (
              <Reveal as="p" className={styles.lead} itemProp="description">
                {renderSegments(firstParagraph)}
              </Reveal>
            )}

            {restParagraphs.map((paragraph, index) => (
              <Reveal
                key={index}
                as="p"
                className={styles.paragraph}
                delay={0.08 + index * 0.06}
              >
                {renderSegments(paragraph)}
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* --- Qualification: is this course for you? --- */}
      <section
        id="per-chi"
        className={styles.perChi}
        aria-label="Per chi è il Percorso ALPS"
      >
        <Reveal className={styles.head}>
          <span className={styles.eyebrow}>{fit.eyebrow}</span>
          <h2 className={styles.title}>{setHeading(fit.title)}</h2>
          <p className={styles.headSubtitle}>{setText(fit.subtitle)}</p>
        </Reveal>

        <div className={styles.detailsGrid}>
          <Reveal className={`${styles.panel} ${styles.panelLight}`}>
            <h3>{setHeading(isForYou.title)}</h3>
            <ul>
              {isForYou.points.map((point, index) => (
                <li key={index}>
                  {ListIcon && <ListIcon className={styles.icon} aria-hidden="true" />}
                  <span>{point}</span>
                </li>
              ))}
            </ul>
          </Reveal>

          <Reveal className={`${styles.panel} ${styles.panelDark}`} delay={0.1}>
            <h3>{setHeading(willHelpYou.title)}</h3>
            <ul>
              {willHelpYou.points.map((point, index) => (
                <li key={index}>
                  {ListIcon && <ListIcon className={styles.icon} aria-hidden="true" />}
                  <span>{point}</span>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>

        <div className={styles.outro}>
          <Reveal className={styles.calloutWrapper}>
            <Callout label="Prossime candidature" icon={CalendarClock}>
              {conclusion}
            </Callout>
          </Reveal>

          <Reveal className={styles.ctaWrapper} delay={0.08}>
            <CtaButton to="register">{cta}</CtaButton>
          </Reveal>
        </div>
      </section>
    </>
  );
};

export default About;
